import React from 'react';

import { FlexProps } from '@mantine/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AdvancedSection } from 'components/SmartAssistantSections/AdvancedSection/AdvancedSection';
import { useAdvancedForms } from 'hooks/SmartAssistantForms/use-advanced-forms';
import { ManageSmartAssistantSectionControlWrapper } from 'pages/ManageSmartAssistant/ManageSmartAssistantControlWrapper';
import { useTranslation } from 'react-i18next';
import { NotificationService } from 'services/notification-service';
import { SmartAssistantService } from 'services/smart-assistant-service';
import { SMART_ASSISTANT_QUERY_KEY } from 'shared/constants/query-keys';
import { SmartAssistant } from 'shared/interfaces/SmartAssistant';

interface AdvancedManagementSectionProps extends FlexProps {
  smartAssistant: SmartAssistant;
}

export function AdvancedManagementSection({ smartAssistant, ...flexProps }: AdvancedManagementSectionProps) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { formToSmartAssistantField, ...advancedForms } = useAdvancedForms(smartAssistant.type, smartAssistant);

  const saveMutation = useMutation({
    mutationFn: SmartAssistantService.updateSmartAssistant,
    onSuccess: (updatedSmartAssistant: SmartAssistant) => {
      queryClient.setQueryData([SMART_ASSISTANT_QUERY_KEY, { smartAssistantId: smartAssistant._id }], () => {
        return updatedSmartAssistant;
      });
      Object.values(advancedForms).forEach((form) => {
        form.resetDirty();
      });

      NotificationService.showResult(true, 'FEED_BACK.SMART_ASSISTANT.SUCCESS.UPDATE_ADVANCED');
    },
    onError: () => NotificationService.showResult(false, 'FEED_BACK.SMART_ASSISTANT.FAILURE.UPDATE_ADVANCED')
  });

  const onSave = () => {
    const validation = Object.values(advancedForms).some((form) => form.validate().hasErrors);
    if (validation) return;

    const formData = Object.entries(advancedForms).reduce((acc, [formKey, form]) => {
      const typedFormKey = formKey as keyof typeof formToSmartAssistantField;
      const fieldKey = formToSmartAssistantField[typedFormKey];
      if (fieldKey && form) {
        return { ...acc, [fieldKey]: form.values };
      }
      return acc;
    }, {});

    saveMutation.mutate({
      id: smartAssistant._id,
      ...formData
    });
  };

  const hasSomeDirtyForms = (): boolean => {
    return Object.values(advancedForms).some((form) => form.isDirty());
  };

  const onCancel = () => {
    Object.values(advancedForms).forEach((form) => {
      form.reset();
    });
  };

  return (
    <ManageSmartAssistantSectionControlWrapper
      title={t('SMART_ASSISTANT.ADVANCED_SECTION.TITLE')}
      hasChanges={hasSomeDirtyForms()}
      loading={saveMutation.isLoading}
      onSave={onSave}
      onCancel={onCancel}>
      <AdvancedSection {...advancedForms} {...flexProps} />
    </ManageSmartAssistantSectionControlWrapper>
  );
}
